import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import { useQuery } from '@tanstack/react-query';
import projectKeys from './keyFactory';
import type { SolarRecommendationDesignInput } from '@/gql/graphql';

const SolarDesignerInvertersQuery = graphql(`
  query SolarDesignerInverters(
    $projectId: ID!
    $panelsCount: Int!
    $panelCapacityWatts: Int!
    $includeBattery: Boolean!
    $batteryQuantity: Int
    $faceCount: Int
    $selectedPanelApplianceId: ID
  ) {
    projectById(id: $projectId) {
      publicId
      solarPotential {
        recommendedInverterAppliances(
          panelsCount: $panelsCount
          panelCapacityWatts: $panelCapacityWatts
          includeBattery: $includeBattery
          batteryQuantity: $batteryQuantity
          faceCount: $faceCount
          selectedPanelApplianceId: $selectedPanelApplianceId
        ) {
          ...SolarRecommendedApplianceFields
        }
        excludedInverterAppliances(
          panelsCount: $panelsCount
          panelCapacityWatts: $panelCapacityWatts
          includeBattery: $includeBattery
          batteryQuantity: $batteryQuantity
          faceCount: $faceCount
          selectedPanelApplianceId: $selectedPanelApplianceId
        ) {
          ...SolarRecommendedApplianceFields
          exclusionReason
          blockingReason
        }
      }
    }
  }
`);

const SolarDesignInvertersQuery = graphql(`
  query SolarDesignInverters(
    $projectId: ID!
    $designId: ID!
    $savedDesign: SolarRecommendationDesignInput
    $panelsCount: Int!
    $panelCapacityWatts: Int!
    $includeBattery: Boolean!
    $batteryQuantity: Int
    $faceCount: Int
    $selectedPanelApplianceId: ID
  ) {
    projectById(id: $projectId) {
      publicId
      solarDesignDraft(designId: $designId) {
        id
        recommendedInverterAppliances(
          savedDesign: $savedDesign
          panelsCount: $panelsCount
          panelCapacityWatts: $panelCapacityWatts
          includeBattery: $includeBattery
          batteryQuantity: $batteryQuantity
          faceCount: $faceCount
          selectedPanelApplianceId: $selectedPanelApplianceId
        ) {
          ...SolarRecommendedApplianceFields
        }
        excludedInverterAppliances(
          savedDesign: $savedDesign
          panelsCount: $panelsCount
          panelCapacityWatts: $panelCapacityWatts
          includeBattery: $includeBattery
          batteryQuantity: $batteryQuantity
          faceCount: $faceCount
          selectedPanelApplianceId: $selectedPanelApplianceId
        ) {
          ...SolarRecommendedApplianceFields
          exclusionReason
          blockingReason
        }
      }
    }
  }
`);

/**
 * Read-only catalogue matching for the panels currently on the canvas. With a
 * `designId` the match is read through the saved design, which a hand-drawn
 * roof has where it has no analysis.
 */
export const useSolarDesignerInverters = (
  projectId: string,
  panelsCount: number,
  capacityWatts: number | null,
  includeBattery: boolean,
  designId?: string,
  faceCount?: number | null,
  selectedPanelApplianceId?: string | null,
  savedDesign?: SolarRecommendationDesignInput,
  savedSizingReady = true,
  batteryQuantity = 1
) => {
  const enabled = panelsCount > 0 && capacityWatts != null && capacityWatts > 0;
  const designScope = [
    ...projectKeys.project(projectId),
    'solarDesignerInverters',
    designId ?? null,
  ];
  const queryFn = async ({ signal }: { signal: AbortSignal }) => {
    const sizing = {
      projectId,
      panelsCount,
      panelCapacityWatts: capacityWatts!,
      includeBattery,
      faceCount,
      selectedPanelApplianceId,
      batteryQuantity,
    };
    if (designId) {
      const result = await gqlClient.request({
        document: SolarDesignInvertersQuery,
        variables: {
          ...sizing,
          designId,
          savedDesign,
        },
        signal,
      });
      return result.projectById.solarDesignDraft;
    }
    const result = await gqlClient.request(SolarDesignerInvertersQuery, sizing);
    return result.projectById.solarPotential;
  };
  type Matches = Awaited<ReturnType<typeof queryFn>>;
  const { data, ...query } = useQuery({
    queryKey: [
      ...designScope,
      panelsCount,
      capacityWatts,
      includeBattery,
      faceCount,
      selectedPanelApplianceId,
      savedDesign,
      batteryQuantity,
    ],
    // Every autosave re-keys this query by revision. Keep showing the same
    // design's last answer while the next one loads, so a save never blanks
    // the inverter lists; `hasCheckedRecommendations` stays false meanwhile.
    placeholderData: (
      previous: Matches | undefined,
      previousQuery?: { queryKey: readonly unknown[] }
    ) =>
      enabled &&
      JSON.stringify(previousQuery?.queryKey.slice(0, designScope.length)) ===
        JSON.stringify(designScope)
        ? previous
        : undefined,
    enabled: enabled && savedSizingReady,
    queryFn,
  });
  // Until the edit is saved the last answer stays on screen (placeholder data
  // of the same design); nothing new is selected from it automatically.
  const current = enabled ? data : undefined;
  return {
    recommendedInverterAppliances: current?.recommendedInverterAppliances ?? [],
    excludedInverterAppliances: current?.excludedInverterAppliances ?? [],
    hasCheckedRecommendations:
      enabled &&
      savedSizingReady &&
      query.isSuccess &&
      !query.isPlaceholderData,
    data,
    ...query,
  };
};
